'use client';

import { useState, useEffect } from 'react';
import { useTranslations } from 'next-intl';
import { useCustomer } from '@cobrastyle/storefront-core/hooks';
import { StoreLink } from '@/components/store';
import type { NavigationItem } from '@cobrastyle/shared-types';
import { cn } from '@cobrastyle/ui';
import { COMPANY } from '@/lib/company';
import type { CategoryPromoSlot } from '@/lib/category-promo-slots';
import { PromoTile } from './promo-tile';
import { MOBILE_HEADER_HEIGHT } from './mobile-header-height';
import { useMobileOverlay } from './mobile-overlay-context';
import { CUSTOMER_SERVICE_LINKS, type CustomerServiceLink } from '@/lib/customer-service';

interface MobileNavigationProps {
  items: NavigationItem[];
  /** Store/locale switcher, shown at the bottom of the menu below a divider. */
  children?: React.ReactNode;
  /** Same promo data the desktop mega menu gets, keyed by category id. */
  promoSlotsByCategory?: Record<string, CategoryPromoSlot[]>;
}

// Same real routes as the desktop utility bar's Kundtjänst dropdown
// (customer-service-menu.tsx), plus opening hours — the design's mobile
// drawer also lists a couple of things that turn out to be real categories
// ("Vapenpaket", "Varumärken") folded in under the Kundtjänst heading; those
// stay with the other categories above instead, since mixing a product
// category into a customer-service list would be a real IA regression, not
// just a visual difference from the canvas.
// Öppettider ahead of the section proper: the utility bar carries it as its own
// link, and that bar is desktop-only, so this drawer is the only place a phone
// can reach it. Everything after comes from the shared list, so the drawer
// cannot fall out of step with the footer, the dropdown and the section sidebar
// — which is exactly what this list had already started to do.
const SERVICE_LINKS: readonly CustomerServiceLink[] = [
  { href: '/oppettider', key: 'openingHours' },
  ...CUSTOMER_SERVICE_LINKS,
];


export function MobileNavigation({ items, children, promoSlotsByCategory }: MobileNavigationProps) {
  const { activeOverlay, openOverlay, closeOverlay } = useMobileOverlay();
  const isOpen = activeOverlay === 'menu';
  const t = useTranslations('nav');
  const tFooter = useTranslations('footer');
  const { customer, isLoggedIn } = useCustomer();

  // Lock body scroll while the menu overlay is open (mirrors MobileSearch).
  useEffect(() => {
    if (!isOpen) return;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  return (
    <div className="lg:hidden">
      {/* Menu Button */}
      <button
        onClick={() => (isOpen ? closeOverlay() : openOverlay('menu'))}
        className="flex h-10 w-10 items-center justify-center rounded-xl text-foreground transition-colors hover:bg-card-hover"
        aria-label={t('menu')}
      >
        <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
          {isOpen ? (
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
          ) : (
            <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5" />
          )}
        </svg>
      </button>

      {isOpen && (
        <>
          <div
            className="fixed inset-x-0 bottom-0 z-40 bg-black/30 backdrop-blur-sm"
            style={{ top: MOBILE_HEADER_HEIGHT }}
            onClick={closeOverlay}
          />

          {/* Mobile Menu */}
          <div
            className="fixed inset-x-0 z-50 overflow-y-auto border-b border-border bg-background p-4"
            style={{ top: MOBILE_HEADER_HEIGHT, maxHeight: `calc(100vh - ${MOBILE_HEADER_HEIGHT}px)` }}
          >
            {/* Account access has no home in the mobile header row itself (the
                design reserves that for a bottom tab bar, not built yet), so
                it lives here instead — first thing in the drawer rather than
                buried after the category list. */}
            <StoreLink
              href={isLoggedIn && customer ? '/konto' : '/konto/logga-in'}
              onClick={closeOverlay}
              className="mb-1 flex items-center gap-3 rounded-xl px-4 py-3 text-base font-medium text-foreground hover:bg-card-hover"
            >
              <svg className="h-5 w-5 text-muted" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5} aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z" />
              </svg>
              {isLoggedIn && customer ? customer.firstname : t('signIn')}
            </StoreLink>

            <nav>
              <ul>
                {items.map((item) => (
                  <MobileNavItem
                    key={item.id}
                    item={item}
                    onNavigate={closeOverlay}
                    promoSlots={promoSlotsByCategory?.[item.id]}
                  />
                ))}
              </ul>
            </nav>

            {/* Kundtjänst — same real routes as the desktop utility bar's
                dropdown, just listed flat instead of in a hover panel. */}
            <div className="mt-5">
              <p className="mb-1 font-mono text-[10px] uppercase tracking-[0.14em] text-accent-foreground">
                {t('customerService')}
              </p>
              {SERVICE_LINKS.map(({ href, key }) => (
                <StoreLink
                  key={href}
                  href={href}
                  onClick={closeOverlay}
                  className="block border-b border-border py-2.5 text-sm text-foreground/75 transition-colors hover:text-foreground"
                >
                  {key === 'openingHours' ? t('openingHours') : tFooter(key)}
                </StoreLink>
              ))}
            </div>

            {/* Contact card — phone + address, the same facts as the
                utility bar dropdown's contact column, condensed into one
                card rather than a full address block. */}
            <div className="mt-[18px] rounded-md border border-border bg-card px-4 py-3.5">
              <a
                href={`tel:${COMPANY.phone.replace(/[^\d+]/g, '')}`}
                className="text-[15px] font-semibold text-foreground"
              >
                {COMPANY.phone}
              </a>
              <p className="mt-0.5 text-xs text-foreground/60">
                {COMPANY.street}, {COMPANY.postalCode} {COMPANY.city}
              </p>
            </div>

            {/* Store / locale switcher, set off with a divider */}
            {children && (
              <div className="mt-4 border-t border-border px-4 pt-4 [&>div]:w-full [&_select]:w-full">
                {children}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

interface MobileNavItemProps {
  item: NavigationItem;
  onNavigate: () => void;
  promoSlots?: CategoryPromoSlot[];
}

function MobileNavItem({ item, onNavigate, promoSlots }: MobileNavItemProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const hasChildren = item.children.length > 0;
  const t = useTranslations('nav');

  // The name navigates and the chevron expands, as two separate controls. The
  // whole row used to be one button that only expanded, which is why the
  // submenu needed a "Visa alla" of its own as its first row: without it the
  // category page had no route out of this drawer at all. Splitting the row
  // retires that link — the drawer now lists every subcategory, so a row whose
  // only job was "there is more than this" had nothing left to say.
  //
  // A leaf category has nothing to open and stays a plain full-width link.
  return (
    <li className="border-b border-border">
      {hasChildren ? (
        <div className="flex items-center">
          <StoreLink
            href={`/${item.urlKey}`}
            onClick={onNavigate}
            className="min-w-0 flex-1 py-[15px] text-[19px] font-semibold text-foreground"
          >
            {item.name}
          </StoreLink>
          <button
            type="button"
            onClick={() => setIsExpanded(!isExpanded)}
            aria-expanded={isExpanded}
            aria-label={t('showSubcategories', { name: item.name })}
            // Its own 44px square rather than padding on the row: this sits
            // beside a link, so the two need an unambiguous boundary for a
            // thumb. `-mr-2.5` keeps the chevron optically on the drawer's
            // right edge while the target stays full width.
            className="-mr-2.5 flex h-11 w-11 flex-none items-center justify-center text-foreground"
          >
            <svg
              className={cn('h-4 w-4 transition-transform', isExpanded && 'rotate-180')}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 9l-7 7-7-7"
              />
            </svg>
          </button>
        </div>
      ) : (
        <StoreLink
          href={`/${item.urlKey}`}
          onClick={onNavigate}
          className="block py-[15px] text-[19px] font-semibold text-foreground"
        >
          {item.name}
        </StoreLink>
      )}
      {hasChildren && isExpanded && (
        <>
          <ul className="ml-4 mt-1 space-y-1 border-l border-border pl-4">
            {item.children.map((child) => (
              <li key={child.id}>
                <StoreLink
                  href={`/${child.urlKey}`}
                  onClick={onNavigate}
                  className="block rounded-xl px-4 py-2.5 text-sm font-medium text-muted-foreground hover:bg-card-hover hover:text-foreground"
                >
                  {child.name}
                </StoreLink>
                {/* The catalogue's third level, which this drawer used to drop
                    on the floor — the desktop panel has shown it since it
                    started grouping, and a level you can only reach on a
                    desktop is not a level.

                    Smaller and quieter directly beneath its parent, the same
                    two signals the desktop panel uses, and with no further
                    indent: this list is already inset twice, and a third step
                    would leave a phone about half a screen to write
                    "Salongsgevär och Småviltsstudsare" in. The row keeps the
                    full py-2.5 of its siblings even though the type is
                    smaller — on a touch screen the tap target is not the place
                    to save space. */}
                {child.children.map((grandchild) => (
                  <StoreLink
                    key={grandchild.id}
                    href={`/${grandchild.urlKey}`}
                    onClick={onNavigate}
                    className="block rounded-xl px-4 py-2.5 text-[12.5px] text-muted hover:bg-card-hover hover:text-foreground"
                  >
                    {grandchild.name}
                  </StoreLink>
                ))}
              </li>
            ))}
          </ul>

          {/* Promo tiles — same data and card as the desktop mega menu's.
              Stacked full-width on phone-size screens; from md (tablet/iPad
              widths, still below the lg breakpoint where this whole drawer
              gives way to the desktop mega menu) there's room for them side
              by side, same as the desktop pair. */}
          {promoSlots && promoSlots.length > 0 && (
            <div className="mb-2 mt-3 grid grid-cols-1 gap-3 md:grid-cols-2">
              {promoSlots.map((slot) => (
                <PromoTile key={slot.linkUrl} slot={slot} onClick={onNavigate} />
              ))}
            </div>
          )}
        </>
      )}
    </li>
  );
}
